
<template>
  <div class="metering-point-info">
    <div class="section-content l">
      <div class="section-header section-header-alt">
        <div class="section-header-left">
          <div class="section-title">MPID INFO :</div>
          <div v-if="isAddonType && hasLinkedMeterWithMpid" class="addon-header-note">
            Attached to meter: <span class="linked-meter-id-header">{{ linkedMeterLDN }}</span>
          </div>
        </div>
        <div class="action-buttons" v-if="displayMeteringPoint">
          <button class="action-button" @click="openEditOverlay">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
            </svg>
            Edit
          </button>
        </div>
      </div>

      <div v-if="isLoading" class="loading-state">
        <div class="loading-spinner"></div>
        <span>Loading metering point data...</span>
      </div>

      <div v-else-if="error && mpidValue && !isAddonType" class="error-state">
        <div class="error-message">{{ error }}</div>
      </div>

      <div v-else-if="displayMeteringPoint" class="device-details-grid">
        <div class="row">
          <div class="col-6">
            <div class="device-info-row">
              <div class="device-info-label">MPID :</div>
              <div class="device-info-value">{{ displayMeteringPoint.mpid }}</div>
            </div>
            <div class="device-info-row">
              <div class="device-info-label">CITY :</div>
              <div class="device-info-value">{{ displayMeteringPoint.city || 'N/A' }}</div>
            </div>
            <div class="device-info-row">
              <div class="device-info-label">STREET :</div>
              <div class="device-info-value">{{ displayMeteringPoint.street || 'N/A' }}</div>
            </div>
            <div class="device-info-row" v-if="displayMeteringPoint.country">
              <div class="device-info-label">COUNTRY :</div>
              <div class="device-info-value">{{ displayMeteringPoint.country }}</div>
            </div>
          </div>

          <div class="col-6">
            <div class="device-info-row">
              <div class="device-info-label">CUSTOMER :</div>
              <div class="device-info-value">{{ displayMeteringPoint.customer_fname || 'N/A' }}</div>
            </div>
            <div class="device-info-row">
              <div class="device-info-label">ZIP CODE :</div>
              <div class="device-info-value">
                {{ displayMeteringPoint.zip_code || displayMeteringPoint.zipcode || 'N/A' }}
              </div>
            </div>
            <div class="device-info-row" v-if="displayMeteringPoint.lat && displayMeteringPoint.lon">
              <div class="device-info-label">COORDINATES :</div>
              <div class="device-info-value">
                {{ formatCoordinates(displayMeteringPoint.lat, displayMeteringPoint.lon) }}
              </div>
            </div>
            <div class="device-info-row" v-if="displayMeteringPoint.created_at">
              <div class="device-info-label">CREATED :</div>
              <div class="device-info-value">{{ formatDate(displayMeteringPoint.created_at) }}</div>
            </div>
          </div>
        </div>
      </div>

      <div v-else-if="!isAddonType" class="no-metering-point">
        <div class="info-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"
               viewBox="0 0 24 24" fill="none" stroke="currentColor"
               stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
        </div>
        <p>This device is not attached to any metering point.</p>
      </div>

      <div v-else-if="isAddonType && !hasActiveMeter" class="no-metering-point">
        <div class="info-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"
               viewBox="0 0 24 24" fill="none" stroke="currentColor"
               stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
        </div>
        <p>This ADDON device is not installed on any meter.</p>
        <p class="secondary-text">Radio devices cannot directly have a metering point. Please install this radio on a meter first.</p>
      </div>

      <div v-else-if="isAddonType && hasActiveMeter && !hasLinkedMeterWithMpid" class="no-metering-point">
        <div class="info-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"
               viewBox="0 0 24 24" fill="none" stroke="currentColor"
               stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
        </div>
        <p>The meter <span class="linked-meter-id">{{ linkedMeterLDN }}</span> that this radio is installed on has no metering point.</p>
        <p class="secondary-text">Please navigate to the meter device to attach it to a metering point.</p>
      </div>
    </div>

    <!-- Edit Overlay -->
    <MpidDetailsInfoEditOverlay
        v-if="showEditOverlay"
        :isVisible="showEditOverlay"
        :meteringPoint="displayMeteringPoint"
        @close="closeEditOverlay"
        @updated="handleMpidUpdated"
    />
  </div>
</template>

<script>
import MpidDetailsInfoEditOverlay from './MpidDetailsInfoEditOverlay.vue';

export default {
  name: 'MpidDetailsInfo',
  components: {
    MpidDetailsInfoEditOverlay
  },
  props: {
    meteringPoint: { type: Object, default: null },
    mpidValue: { type: String, default: '' },
    isLoading: { type: Boolean, default: false },
    error: { type: String, default: '' },
    deviceId: { type: String, required: true },
    deviceType: { type: String, default: '' },
    linkedMeteringPoint: { type: Object, default: null },
    linkedMeterLDN: { type: String, default: '' },
    hasActiveMeter: { type: Boolean, default: false }
  },
  data() {
    return {
      showEditOverlay: false
    };
  },
  computed: {
    isAddonType() {
      return this.deviceType && this.deviceType.toUpperCase() === 'ADDON';
    },
    hasLinkedMeterWithMpid() {
      return !!this.linkedMeteringPoint && !!this.linkedMeterLDN;
    },
    // Prefer real meteringPoint passed from page; only fallback if truly absent.
    displayMeteringPoint() {
      if (this.isAddonType && this.hasLinkedMeterWithMpid) {
        return this.linkedMeteringPoint;
      }
      if (!this.isAddonType && this.meteringPoint) {
        // tolerate DB key variants just in case page didn't normalize
        return {
          ...this.meteringPoint,
          zipcode: this.meteringPoint.zipcode ?? this.meteringPoint.zip_code ?? null,
          zip_code: this.meteringPoint.zip_code ?? this.meteringPoint.zipcode ?? null,
          customer_fname: this.meteringPoint.customer_fname ?? this.meteringPoint.customerfname ?? null,
          created_at: this.meteringPoint.created_at ?? this.meteringPoint.createdat ?? null
        };
      }
      if (!this.isAddonType && this.mpidValue) {
        return {
          mpid: this.mpidValue,
          city: null,
          street: null,
          country: null,
          customer_fname: null,
          zip_code: null,
          lat: null,
          lon: null,
          created_at: null
        };
      }
      if (this.isAddonType && this.hasActiveMeter && !this.hasLinkedMeterWithMpid) {
        return {
          mpid: '—',
          city: null,
          street: null,
          country: null,
          customer_fname: null,
          zip_code: null,
          lat: null,
          lon: null,
          created_at: null
        };
      }
      return null;
    }
  },
  methods: {
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return 'Invalid Date';
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
      } catch (error) {
        console.error('Error formatting date:', error);
        return 'Error';
      }
    },
    formatCoordinates(lat, lon) {
      const numLat = parseFloat(lat);
      const numLon = parseFloat(lon);
      if (isNaN(numLat) || isNaN(numLon)) return 'N/A';
      return `${numLat.toFixed(4)}, ${numLon.toFixed(4)}`;
    },
    openEditOverlay() {
      this.showEditOverlay = true;
    },
    closeEditOverlay() {
      this.showEditOverlay = false;
    },
    handleMpidUpdated(updatedMeteringPoint) {
      this.$emit('mpid-updated', updatedMeteringPoint);
      this.closeEditOverlay();
    }
  }
};
</script>

<style scoped>
/* Metering point info container */
.metering-point-info {
  height: 100%;
}

.section-content {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
}

.section-header-alt {
  margin-top: 80px;
}

.section-header-left {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
}

.section-title {
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: white;
  display: inline-block;
}

.addon-header-note {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.7);
  font-style: italic;
}

.linked-meter-id-header {
  font-weight: 600;
  color: rgba(79, 195, 247, 0.9);
  font-style: normal;
}

/* Device details grid styling */
.device-details-grid {
  margin-top: 35px;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
}

.device-info-row {
  margin-bottom: 25px;
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.device-info-label {
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.05em;
  opacity: 0.6;
  text-align: left;
  text-transform: uppercase;
}

.device-info-value {
  font-size: 11px;
  font-weight: 500;
}

/* Action button styles */
.action-buttons {
  display: flex;
  gap: 10px;
  align-self: flex-start;
}

.action-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: none;
  border: none;
  color: white;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  padding: 6px 12px;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.action-button:hover:not(:disabled) {
  background-color: rgba(255,255,255,0.1);
}

.action-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Loading state styles */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  gap: 15px;
}

.loading-spinner {
  width: 30px;
  height: 30px;
  border: 3px solid rgba(255,255,255,0.1);
  border-top: 3px solid rgba(255,255,255,0.8);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Error state styles */
.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  gap: 15px;
}

.error-message {
  color: #ff8a80;
  text-align: center;
}

/* No metering point state */
.no-metering-point {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  opacity: 0.8;
  text-align: center;
}

.info-icon {
  color: rgba(255,255,255,0.6);
  margin-bottom: 16px;
}

.no-metering-point p {
  font-size: 13px;
  max-width: 80%;
  margin-bottom: 8px;
}

/* Responsive adjustments */
@media (max-width: 767.98px) {
  .section-header-alt {
    margin-top: 60px;
  }

  .device-info-row {
    margin-bottom: 15px;
  }
}

.linked-meter-id {
  font-weight: 600;
  color: rgba(79, 195, 247, 0.9);
}

.secondary-text {
  font-size: 11px !important;
  opacity: 0.7;
  margin-top: 6px;
  max-width: 80%;
}

.l {
  padding-left: 50px;
}
</style>